import React from 'react';
import { ShieldCheck, Target, ClipboardCheck, Clock } from 'lucide-react';
const CONTENT_IMG = 'https://cdn.magicpatterns.com/uploads/cm2dF27V2JyW3M21uejgG8/Crisis-Planning.png';
export function MainContentSection() {
  return <section className="py-16 md:py-24 bg-brand-black" data-id="element-499">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-500">
        {/* ── OVERVIEW: text + image paired ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20 md:mb-24" data-id="element-501">
          {/* Text */}
          <div data-id="element-502">
            <div className="text-xs font-bold text-brand-teal uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-503">
              <span className="w-8 h-0.5 bg-brand-teal" data-id="element-504" />
              Be Ready Before It Matters
            </div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-brand-white uppercase tracking-wide mb-6 leading-tight" data-id="element-505">
              Turning Uncertainty Into Preparedness
            </h2>
            <div className="prose prose-invert prose-lg text-brand-gray-300 leading-relaxed" data-id="element-506">
              <p data-id="element-507">
                Critical incidents rarely announce themselves. When they happen,
                the difference between chaos and control comes down to the
                planning done long before the first alarm. Medina Tactical
                Dynamics works alongside law enforcement agencies, corporations,
                and institutions to build response strategies that hold up when
                the pressure is real.
              </p>
              <p data-id="element-508">
                We assess where you stand today, identify the gaps that put
                people and operations at risk, and deliver clear, actionable
                plans your team can execute with confidence — backed by 24/7
                support whenever you need us.
              </p>
            </div>

            {/* Quick stats / highlights */}
            <div className="grid grid-cols-2 gap-4 mt-8" data-id="element-509">
              <div className="flex items-start gap-3 p-4 border border-brand-gray-800 bg-brand-gray-900" data-id="element-510">
                <ClipboardCheck className="w-5 h-5 text-brand-teal flex-shrink-0 mt-0.5" data-id="element-511" />
                <div data-id="element-512">
                  <p className="text-sm font-bold text-brand-white uppercase tracking-wide" data-id="element-513">
                    Policy Review
                  </p>
                  <p className="text-xs text-brand-gray-400 mt-1 leading-relaxed" data-id="element-514">
                    Gap analysis of your current plans
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-4 border border-brand-gray-800 bg-brand-gray-900" data-id="element-515">
                <Clock className="w-5 h-5 text-brand-teal flex-shrink-0 mt-0.5" data-id="element-516" />
                <div data-id="element-517">
                  <p className="text-sm font-bold text-brand-white uppercase tracking-wide" data-id="element-518">
                    24/7 Support
                  </p>
                  <p className="text-xs text-brand-gray-400 mt-1 leading-relaxed" data-id="element-519">
                    Before, during, and after an incident
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative p-2 border border-brand-gray-700 bg-brand-gray-900" data-id="element-520">
            <img src={CONTENT_IMG} alt="Strategic Planning for Crisis Situations" className="block w-full h-auto object-cover" data-id="element-521" />
            <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-brand-teal z-10" data-id="element-522" />
            <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-brand-teal z-10" data-id="element-523" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-brand-teal z-10" data-id="element-524" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-brand-teal z-10" data-id="element-525" />
          </div>
        </div>

        {/* ── HOW WE HELP heading ── */}
        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16" data-id="element-526">
          <h2 className="text-2xl md:text-3xl font-extrabold text-brand-white uppercase tracking-wide mb-4" data-id="element-527">
            How We Help
          </h2>
          <div className="w-16 h-0.5 bg-brand-teal mx-auto" data-id="element-528" />
        </div>

        {/* ── TWO CAPABILITY BOXES ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch" data-id="element-529">
          {/* Strategic Preparedness */}
          <div className="bg-brand-gray-900 p-8 md:p-10 border border-brand-gray-800 h-full" data-id="element-530">
            <div className="w-12 h-12 bg-brand-teal/10 border border-brand-teal/30 flex items-center justify-center mb-8" data-id="element-531">
              <ShieldCheck className="w-6 h-6 text-brand-teal" data-id="element-532" />
            </div>
            <h3 className="text-2xl font-bold text-brand-white uppercase tracking-wide mb-6" data-id="element-533">
              Strategic Preparedness for Critical Incidents
            </h3>
            <div className="prose prose-invert prose-lg text-brand-gray-300 leading-relaxed" data-id="element-534">
              <p data-id="element-535">
                For many law enforcement agencies and corporate organizations,
                preparation and planning for critical incidents often take a
                back seat—until a crisis arises. Our team of highly experienced
                consultants specializes in evaluating your existing policies,
                identifying potential gaps, and implementing strategic
                enhancements to ensure your organization remains fully prepared.
              </p>
              <p data-id="element-536">
                We offer comprehensive policy development, continuous updates,
                and the creation of advanced operational plans that integrate
                the latest methodologies and cutting-edge technologies tailored
                to your specific needs. If your current plans require
                modernization or optimization, we are here to help.
              </p>
            </div>
          </div>

          {/* Proactive Strategy */}
          <div className="bg-brand-gray-900 p-8 md:p-10 border border-brand-gray-800 h-full" data-id="element-537">
            <div className="w-12 h-12 bg-brand-teal/10 border border-brand-teal/30 flex items-center justify-center mb-8" data-id="element-538">
              <Target className="w-6 h-6 text-brand-teal" data-id="element-539" />
            </div>
            <h3 className="text-2xl font-bold text-brand-white uppercase tracking-wide mb-6" data-id="element-540">
              Proactive Strategy Development for Operational Readiness
            </h3>
            <div className="prose prose-invert prose-lg text-brand-gray-300 leading-relaxed" data-id="element-541">
              <p data-id="element-542">
                Developing effective crisis response strategies can be
                challenging, particularly in industries where critical incidents
                occur infrequently. This lack of immediate crisis exposure often
                results in outdated or insufficient preparedness measures. Our
                team ensures that your organization remains ahead of potential
                threats by designing, refining, and implementing
                forward-thinking strategies that yield tangible, positive
                outcomes when critical moments arise.
              </p>
              <p data-id="element-543">
                We provide tailored operational plans, ensuring that you and
                your staff remain well-informed, fully equipped, and always
                ready to respond effectively. By leveraging industry-leading
                expertise and the most current strategic advancements, we help
                you maintain a state of readiness that minimizes risks and
                maximizes efficiency.
              </p>
            </div>
          </div>
        </div>

        {/* ── CLOSING CTA ── */}
        <div className="mt-16 md:mt-20 p-8 md:p-10 border-l-4 border-brand-teal bg-brand-gray-900/50 text-center max-w-3xl mx-auto" data-id="element-544">
          <p className="text-lg md:text-xl text-brand-white font-medium leading-relaxed mb-0" data-id="element-545">
            Take a proactive approach to risk management. Contact us today to
            review your current plans at{' '}
            <a href="mailto:Info@apc360zone.com" className="text-brand-teal hover:text-brand-teal-light transition-colors font-bold" data-id="element-546">
              Info@apc360zone.com
            </a>{' '}
            or{' '}
            <a href="tel:18666343373" className="text-brand-teal hover:text-brand-teal-light transition-colors font-bold" data-id="element-547">
              1-866-634-3373
            </a>
            .
          </p>
        </div>
      </div>
    </section>;
}